import { useRef, type KeyboardEvent } from 'react'
import { neighbourKey, nextOpenKey } from '@/app/document-review/approval-navigation'
import { approvalKeyOf } from '@/app/document-review/approval-row'

export type ApprovalKeyboardParams = {
  keys: readonly string[]
  isOpen: (key: string) => boolean
  finishKey: string | null
  onConfirm: (key: string) => void
  onToggle: (key: string) => void
  onEdit: (key: string) => void
  onFinish: () => void
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'])

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return EDITABLE_TAGS.has(target.tagName) || target.isContentEditable
}

export function useApprovalKeyboard({
  keys,
  isOpen,
  finishKey,
  onConfirm,
  onToggle,
  onEdit,
  onFinish
}: ApprovalKeyboardParams) {
  const containerRef = useRef<HTMLDivElement>(null)

  const focusKey = (key: string | null) => {
    if (key === null) return
    const rows = containerRef.current?.querySelectorAll<HTMLElement>('[data-approval-key]') ?? []
    for (const row of rows) {
      if (row.dataset['approvalKey'] === key) {
        row.focus()
        row.scrollIntoView({ block: 'nearest' })
        return
      }
    }
  }

  const keyAfter = (key: string) => nextOpenKey(keys, key, (one) => one !== key && isOpen(one)) ?? finishKey ?? key

  const focusAfter = (key: string) => {
    const next = keyAfter(key)
    window.setTimeout(() => focusKey(next), 0)
  }

  const focusNextOpen = () => focusKey(nextOpenKey(keys, null, isOpen))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      onFinish()
      return
    }
    const current = approvalKeyOf(event.target)
    if (current !== null && current === finishKey) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
        focusKey(neighbourKey(keys, current, event.key === 'ArrowDown' ? 1 : -1))
      }
      return
    }
    if (isEditableTarget(event.target)) return

    switch (event.key) {
      case 'Enter': {
        if (current === null) return
        event.preventDefault()
        onConfirm(current)
        const next = keyAfter(current)
        if (next === finishKey) focusAfter(current)
        else focusKey(next)
        return
      }
      case ' ':
        if (current === null) return
        event.preventDefault()
        onToggle(current)
        return
      case 'e':
      case 'F2':
        if (current === null) return
        event.preventDefault()
        onEdit(current)
        return
      case 'ArrowDown':
        if (current === null) return
        event.preventDefault()
        focusKey(neighbourKey(keys, current, 1))
        return
      case 'ArrowUp':
        if (current === null) return
        event.preventDefault()
        focusKey(neighbourKey(keys, current, -1))
        return
      default:
    }
  }

  return { containerRef, focusKey, focusAfter, focusNextOpen, onKeyDown }
}
